import React, { useEffect, useRef, useState } from 'react'

interface QuoteBounceProps {
  /** How many quote icons to render */
  quoteCount?: number
  /** Size of each quote icon in pixels */
  size?: number
}

interface QuoteState {
  x: number // position ratio (0-1) from left
  y: number // position ratio (0-1) from top
  vx: number // velocity ratio per second (portion of container per sec)
  vy: number
}

// The bouncing animation runs on every animation frame and updates React state.
// With a low quoteCount (default 4) this is performant and avoids external deps.
export const QuoteBounce: React.FC<QuoteBounceProps> = ({ quoteCount = 4, size = 32 }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  // Store quotes in state so React re-renders when positions change.
  const [quotes, setQuotes] = useState<QuoteState[]>(() =>
    Array.from({ length: quoteCount }, () => {
      const angle = Math.random() * Math.PI * 2
      // Slower movement: between 0.1–0.25 container lengths per second
      const speed = 0.1 + Math.random() * 0.15 // portion of container per sec
      return {
        x: Math.random(),
        y: Math.random(),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
      }
    })
  )

  useEffect(() => {
    let last = performance.now()
    let frameId: number

    const update = (time: number) => {
      const dt = (time - last) / 1000 // seconds since last frame
      last = time

      setQuotes((prev) =>
        prev.map((q) => {
          let { x, y, vx, vy } = q
          x += vx * dt
          y += vy * dt

          // Bounce off the walls
          if (x < 0) {
            x = 0
            vx = Math.abs(vx)
          } else if (x > 1) {
            x = 1
            vx = -Math.abs(vx)
          }
          if (y < 0) {
            y = 0
            vy = Math.abs(vy)
          } else if (y > 1) {
            y = 1
            vy = -Math.abs(vy)
          }
          return { x, y, vx, vy }
        })
      )

      frameId = requestAnimationFrame(update)
    }

    frameId = requestAnimationFrame(update)
    return () => cancelAnimationFrame(frameId)
  }, [])

  const icon = (
    <svg viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path
        d="M10.5015 11H6.50146C6.23625 11 5.98189 10.8946 5.79436 10.7071C5.60682 10.5196 5.50146 10.2652 5.50146 10V7C5.50146 6.73478 5.60682 6.48043 5.79436 6.29289C5.98189 6.10536 6.23625 6 6.50146 6H9.50146C9.76668 6 10.021 6.10536 10.2086 6.29289C10.3961 6.48043 10.5015 6.73478 10.5015 7V13C10.5015 15.667 9.16846 17.333 6.50146 18"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.5015 11H15.5015C15.2362 11 14.9819 10.8946 14.7944 10.7071C14.6068 10.5196 14.5015 10.2652 14.5015 10V7C14.5015 6.73478 14.6068 6.48043 14.7944 6.29289C14.9819 6.10536 15.2362 6 15.5015 6H18.5015C18.7667 6 19.021 6.10536 19.2086 6.29289C19.3961 6.48043 19.5015 6.73478 19.5015 7V13C19.5015 15.667 18.1685 17.333 15.5015 18"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none overflow-hidden">
      {quotes.map((q, i) => (
        <div
          key={i}
          className="absolute text-[#FF4A8E]/20"
          style={{
            width: size,
            height: size,
            left: `${q.x * 100}%`,
            top: `${q.y * 100}%`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          {icon}
        </div>
      ))}
    </div>
  )
}

export default QuoteBounce
